<!--
Copyright (c) 2001-present X-Cart Holdings LLC. All rights reserved.
See https://www.x-cart.com/license-agreement.html for license details.
-->

<template>
  <div
    v-if="enabled"
    :class="isActive ? ' active' : ''"
    class="app-search-bar"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave">
    <div class="input-group">
      <search-bar-dropdown
        :menu-items="menuItems"
        :is-active="isActive"
        @searchTypeSelected="clickSearchCodeHandler"/>
      <input
        ref="searchInput"
        v-model="currentSearchFilter"
        :placeholder="$t('Search in My Apps')"
        class="form-control"
        type="text"
        @keyup.13="searchStringInputHandler">
      <div
        class="clear-search-button"
        @click="clearClickHandler"><i class="fa-times" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import RoutingHelpers from '../mixins/RoutingHelpers';
import SearchBarDropdown from '../input/SearchBarDropdown';
import Icon from './Icon';

export default {
  name: 'SearchBar',
  components: {
    Icon,
    SearchBarDropdown,
  },
  mixins: [RoutingHelpers],
  data() {
    return {
      currentSearchFilter: '',
      over: false,
      searchCode: 'my_apps',
      menuItems: { // \XLite\View\HeaderSearch::defineMenuItems()
        order: { name: this.$t('Orders'), placeholder: this.$t('Search in orders'), urlQuery: 'order_list&substring=' },
        product: { name: this.$t('Products'), placeholder: this.$t('Search in products'), urlQuery: 'product_list&substring=' },
        profile: { name: this.$t('Users'), placeholder: this.$t('Search in users'), urlQuery: 'profile_list&pattern=' },
        my_apps: { name: this.$t('My Apps'), placeholder: this.$t('Search in My Apps'), urlQuery: '' },
      },
    };
  },
  computed: {
    ...mapState({
      actualFilter(state) {
        return state.modulesData.filter;
      },
      transitions(state) {
        return state.scenarios.common.transitions;
      },
    }),
    enabled() {
      return this.$route.path.substring(1).split('/')[0] !== 'upgrade';
    },
    isInputEmpty() {
      return this.currentSearchFilter === '';
    },
    isActive() {
      return !this.isInputEmpty || this.over;
    },
  },
  beforeMount() {
    if (!_.isEmpty(this.searchQuery())) {
      this.currentSearchFilter = this.searchQuery().search;
    }
    this.toggleBaseSearchBar();
  },
  updated() {
    this.toggleBaseSearchBar();
  },
  methods: {
    toggleBaseSearchBar() {
      const header = document.getElementById('header');
      if (header) {
        const commonSearchBar = header.getElementsByClassName('header-search');
        if (
          commonSearchBar
            && commonSearchBar.length === 1
        ) {
          commonSearchBar[0].style.display =
              window.location.hash.indexOf('#/installed-addons') === 0
              || window.location.hash.indexOf('#/success-rebuild') === 0
              || window.location.hash === '#/'
                ? 'none' : 'block';
        }
      }
    },
    searchStringInputHandler() {
      if (
        this.searchCode
          && this.searchCode !== 'my_apps'
          && Object.keys(this.menuItems).includes(this.searchCode)
      ) {
        const substring = this.currentSearchFilter;
        this.currentSearchFilter = '';
        this.$router.push(
          {
            name: 'redirect-to-main-admin-area',
            params: { targetString: this.menuItems[this.searchCode].urlQuery, substring },
          },
        );
        return;
      }

      if (this.currentSearchFilter) {
        this.$router.push({ path: '/' }).catch(() => {});

        const searchQuery = { search: this.currentSearchFilter };

        this.queryPatch(searchQuery);

        const newQuery = Object.assign(this.actualFilter, { page: 1, ...searchQuery });

        this.$store.commit('modulesData/SET_FILTER', newQuery);

        this.$store.dispatch(
          'modulesData/fetchModules',
          {
            transitions: this.transitions,
            shade: true,
          },
        );

        this.$refs.searchInput.blur();
      } else {
        this.queryPatch({}, ['search']);
        this.$refs.searchInput.focus();
      }
    },
    clickSearchCodeHandler(selectedCode) {
      this.searchCode = selectedCode;
      this.$refs.searchInput.placeholder = this.menuItems[selectedCode].placeholder;
      this.$refs.searchInput.focus();
    },
    searchQuery() {
      return _.pick(this.$route.query, ['search']) || {};
    },
    clearClickHandler() {
      this.currentSearchFilter = '';

      this.queryPatch({}, ['search']);

      const newQuery = Object.assign(this.actualFilter, { search: null });

      this.$store.commit('modulesData/SET_FILTER', newQuery);

      this.$store.dispatch(
        'modulesData/fetchModules',
        {
          transitions: this.transitions,
          shade: true,
        },
      );
      this.$refs.searchInput.focus();
    },
    onMouseEnter() {
      this.over = true;
    },
    onMouseLeave() {
      this.over = false;
    },
  },
};
</script>

<style lang="scss">
  @import '../../stylesheets/common';
  $header-search-total-width: 500px;
  $color-grey-light-with-02-opacity: rgba(171, 171, 171, 0.2);
  $color-light-blue-with-005-opacity: rgba($blue_button, 0.05);


  #search-bar {
    margin-left: auto;
  }

  .app-search-bar {
    position: relative;
    display: flex;
    width: $header-search-total-width;
    align-items: center;
    background-color: $color-light-blue-with-005-opacity;
    color: $light-gray-color;

    &.active,
    &:hover {
      background-color: $app-header-background-hover;

      input:not([type=radio]):not([type=checkbox]) {
        &::placeholder {
          color: $white-color;
          opacity: 1;
        }
      }

      i {
        color: $white-color;
        opacity: 1;
      }
    }

    div {
      padding-right: 5px;

      svg {
        position: absolute;
      }

      i {
        margin-left: 30px;
      }

      &.input-group-btn:after {
        content: " ";
        position: absolute;
        border-left: 1px $color-grey-light-with-02-opacity solid;
        top: 25%;
        right: 0;
        height: 50%;
        margin-top: auto;
        margin-bottom: auto;
      }
    }

    input:not([type=radio]):not([type=checkbox]) {
      width: $header-search-total-width - 30px;
      text-overflow: ellipsis;
      box-shadow: none;
      overflow: hidden;
      margin: 0;
      padding: ($input-padding-vertical * .25)
        ($input-padding-horizontal * 0.75)
        ($input-padding-vertical * .25)
        ($input-padding-horizontal * .25);
      height: $input-height-base;
      line-height: $input-height-base;
      border: 0;
      z-index: 2;
      color: $white-color;
      background: transparent;
      font-size: $font-size-small;

      &::placeholder {
        color: $light-gray-color;
      }
    }

    .clear-search-button {
      appearance: none;
      cursor: pointer;
      padding: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      right: 25px;
      top: 50%;
      z-index: 10;
      transform: translateY(-50%);
      background: transparent;
      border: 0;
      color: $light-gray-color;

      &:hover {
        color: $white-color;
      }

      i {
        &:before {
          line-height: 1;
          font-size: 13px;
        }
      }
    }

    .search-button {
      appearance: none;
      background: transparent;
      border: 0;
      padding: 0;
      width: 2 * $rhythmic-unit;
      height: 2 * $rhythmic-unit;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        &:before {
          font-size: 11px;
        }
      }
    }

    @media (max-width: 900px) {
      & {
        margin-right: $margin-base / 2;
      }

      input:not([type=radio]):not([type=checkbox]) {
        width: auto;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/block/SearchBar.vue